import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { DecimalPipe } from "@angular/common";
import {
  Directive,
  DoCheck,
  ElementRef,
  forwardRef,
  HostListener,
  Input
} from "@angular/core";

export const NUMBER_DIRECTIVE_PROVIDER: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => NumberInputDirective),
  multi: true
};

@Directive({
  selector: "[cNumber]",
  providers: [NUMBER_DIRECTIVE_PROVIDER, DecimalPipe]
})
export class NumberInputDirective implements ControlValueAccessor, DoCheck {
  private _onChange: Function;
  private _onTouched: Function;
  private _decimalSep = -1;
  private _originalValue: number;

  @Input("cFormat") format = "1.2-2";
  @Input("cAlign") align = "right";
  @Input("cAllowNegative") allowNegative = true;
  @Input("cNegativeColor") negativeColor = "";

  constructor(
    private _elementRef: ElementRef,
    private _decimalPipe: DecimalPipe
  ) { }

  ngDoCheck() {
    this._elementRef.nativeElement.style.textAlign = this.align;
  }

  registerOnChange(fn: Function) {
    this._onChange = fn;
  }

  registerOnTouched(fn: Function) {
    this._onTouched = fn;
  }

  setDisabledState(value: boolean) {
    this._elementRef.nativeElement.disabled = value;
  }

  writeValue(value: number) {
    this._originalValue = value;
    this._elementRef.nativeElement.value = this.applyMask();
  }

  @HostListener("focus", ["$event"])
  onFocus(event: any) {
    event.target.value = this.clearMask();
    this._elementRef.nativeElement.select();
  }

  @HostListener("blur", ["$event"])
  onBlur(event: any) {
    event.target.value = this.applyMask();
    this._onTouched();
  }

  @HostListener("input", ["$event"])
  onInput(event: any) {
    if (event.target.value.indexOf("-") > -1) {
      event.target.value = "-" + event.target.value.replace(/-/g, "");
    }

    if (event.target.value.indexOf("+") > -1) {
      event.target.value = event.target.value
        .replace("+", "")
        .replace(/-/g, "");
    }

    this._originalValue = parseFloat(event.target.value);

    if (isNaN(this._originalValue)) {
      this._originalValue = 0;
    }

    if (this._originalValue < 0 && this.negativeColor !== "") {
      this._elementRef.nativeElement.style.color = this.negativeColor;
    } else {
      this._elementRef.nativeElement.style.color = "";
    }

    this._decimalSep = event.target.value.indexOf(".");
    this._onChange(this._originalValue);
  }

  @HostListener("keypress", ["$event"])
  onKeypress(event: any): boolean {
    const charCode = event.which ? event.which : event.keyCode;

    if (
      !(
        (charCode >= 48 && charCode <= 57) ||
        (charCode === 46 && this._decimalSep === -1) ||
        ((this.allowNegative && charCode === 45) || charCode === 43)
      )
    ) {
      return false;
    }

    return true;
  }

  applyMask(): string {
    try {
      return this._decimalPipe.transform(this._originalValue, this.format);
    } catch (e) {
      return "";
    }
  }

  clearMask(): string {
    try {
      return this._originalValue.toString();
    } catch (e) {
      return "";
    }
  }
}
